import * as React from "react";
import { styled } from "@mui/material/styles";
import { Typography, Box, Grid, useTheme } from "@mui/material/";

interface StyledPaginationItemProps {
  "aria-selected": boolean;
  value: number;
}

const StyledPaginationItem = styled(Box)<StyledPaginationItemProps>(
  ({ theme, ["aria-selected"]: ariaSelected }) => ({
    display: "inline-block",
    marginTop: "auto",
    fontFamily: theme.typography.fontFamily,
    fontWeight: "normal",
    fontSize: "1.25rem",
    lineHeight: "1.5rem",
    color: theme.colors.black,
    textDecoration: ariaSelected ? "underline" : "none",
    "&:hover": {
      cursor: "pointer",
    },
  }),
);

interface TablePaginationProps {
  pageLimit: number;
  count: number;
  page?: number;
  onChange?: (page: number) => void;
}

const TablePagination: React.FC<TablePaginationProps> = ({
  pageLimit,
  count,
  page = 1,
  onChange,
}) => {
  const theme = useTheme();

  const handleChange = (value: number) => {
    onChange && onChange(value);
  };

  const totalPages = Math.ceil(count / pageLimit);
  const delta = 2;

  const getPageNumbers = () => {
    const currentPage = page || 1;
    const defaultLeft = currentPage - delta;
    const defaultRight = currentPage + delta + 1;
    let left: number;
    let right: number;

    switch (defaultLeft) {
      case -1:
        right = currentPage + delta + 3;
        break;
      case 0:
        right = currentPage + delta + 2;
        break;
      default:
        right = currentPage + delta + 1;
        break;
    }
    switch (defaultRight) {
      case totalPages + 3:
        left = currentPage - delta - 2;
        break;
      case totalPages + 2:
        left = currentPage - delta - 1;
        break;
      default:
        left = currentPage - delta;
        break;
    }

    const rangeWithoutDots: number[] = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i >= left && i < right) {
        rangeWithoutDots.push(i);
      }
    }

    return rangeWithoutDots;
  };

  const pageNumbers = getPageNumbers();

  return (
    <Box width="fit-content" ml="auto" mt="4rem">
      <Grid container alignContent="center" gap="0.625rem">
        {page > 1 && (
          <Grid item>
            <Typography
              onClick={() => handleChange(page + -1)}
              mr="1.25rem"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: "normal",
                fontSize: "1.25rem",
                lineHeight: "1.5rem",
                color: theme.colors.black,
                "&:hover": {
                  cursor: "pointer",
                },
              }}
            >
              Previous
            </Typography>
          </Grid>
        )}
        {pageNumbers.map((pageNumber, index) => {
          return (
            <React.Fragment key={`pageNum-${pageNumber}`}>
              <Grid item>
                <StyledPaginationItem
                  aria-selected={pageNumber === page}
                  onClick={() => handleChange(pageNumber)}
                  value={pageNumber}
                >
                  {pageNumber}
                </StyledPaginationItem>
              </Grid>
              {index + 1 !== pageNumbers.length &&
                pageNumber !== totalPages && (
                  <Grid item>
                    <Box
                      width="2px"
                      height="1.5rem"
                      bgcolor={theme.colors.black}
                    ></Box>
                  </Grid>
                )}
            </React.Fragment>
          );
        })}
        {page < totalPages && (
          <Grid item>
            <Typography
              onClick={() => handleChange(page + 1)}
              ml="1.25rem"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: "normal",
                fontSize: "1.25rem",
                lineHeight: "1.5rem",
                color: theme.colors.black,
                "&:hover": {
                  cursor: "pointer",
                },
              }}
            >
              Next
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default TablePagination;
